import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bike, Mountain, Leaf, Recycle, Calendar, MapPin, ArrowRight, Droplets, Wind, Activity, Heart, Users, Target, TrendingUp, Building, Handshake, Trophy, Clock, CheckCircle, Star, Zap, Map, X, Mail } from 'lucide-react';
import { IMAGES } from '@/assets/images';
import { EVENT_CONFIG } from '@/lib/index';
import { CountdownTimer } from '@/components/CountdownTimer';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog';
const springTransition = {
  type: 'spring',
  stiffness: 100,
  damping: 20
};
export default function Home() {
  const [isRouteMapOpen, setIsRouteMapOpen] = useState(false);
  return <div className="flex flex-col w-full">
      {/* #hero Section */}
      <section id="hero" className="relative flex flex-col items-center justify-center pt-20">
        <div className="relative w-full">
          <img src={IMAGES.CABECERA_127} alt="Mountain Bike y Ciclismo Carretera Ibiza - Ibiza Bike Epic 2026 Balanced Ride Weekend" className="w-full h-auto object-contain" />
          <div className="absolute inset-0 bg-gradient-to-b from-background/20 via-transparent to-background/40" />
          
          {/* Botón Desliza para explorar - Posicionado al 30% desde el final */}
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 1.5
        }} className="absolute bottom-[18%] md:bottom-[30%] left-1/2 transform -translate-x-1/2 z-10 flex flex-col items-center gap-2 md:gap-4 animate-bounce">
            <div className="bg-white/10 backdrop-blur-sm px-2 py-1 md:px-6 md:py-3 rounded-full border border-white/20">
              <span className="font-mono text-xs md:text-lg lg:text-xl text-white uppercase tracking-widest drop-shadow-lg whitespace-nowrap">
                Desliza para explorar
              </span>
            </div>
            <div className="bg-accent/20 backdrop-blur-sm p-1 md:p-3 rounded-full border border-accent/30">
              <ArrowRight className="rotate-90 w-3 h-3 md:w-6 md:h-6 text-accent drop-shadow-lg" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Countdown Section */}
      <section className="py-8 sm:py-12 md:py-16 lg:py-20 bg-gradient-to-b from-primary/5 to-background relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/4 w-32 h-32 md:w-48 md:h-48 bg-accent/10 rounded-full blur-3xl" />
          <div className="absolute top-1/4 right-1/3 w-24 h-24 md:w-36 md:h-36 bg-primary/10 rounded-full blur-2xl" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="flex justify-center items-center w-full">
            <div className="w-full max-w-2xl">
              <CountdownTimer />
            </div>
          </motion.div>
        </div>
      </section>

      {/* #evento Section */}
      <section id="evento" className="py-24 bg-card">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{
            opacity: 0,
            x: -50
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={springTransition}>
              <Badge className="mb-4 bg-accent text-accent-foreground px-4 py-1 text-sm font-mono">
                BALANCED RIDE WEEKEND
              </Badge>
              <h2 className="text-4xl md:text-6xl font-black mb-8 leading-none">
                NO ES UNA PRUEBA. ES UN DESAFÍO.
              </h2>
              <div className="space-y-6 text-lg text-muted-foreground font-serif">
                <p>
                  Vive el único evento de mountain bike y ciclismo de carretera que une montaña y asfalto en un mismo fin de semana en Ibiza. Dos disciplinas. Una isla. Un desafío épico.
                </p>
                <div className="space-y-4">
                  <div>
                    <h4 className="text-xl font-bold text-foreground mb-2">1ª Etapa - Sábado 31 Oct 2026 - Mountain Bike</h4>
                    <p className="text-muted-foreground">Mountain bike técnico por los mejores senderos de Ibiza, con desnivel y naturaleza mediterránea pura.</p>
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-foreground mb-2">2ª Etapa - Domingo 1 Nov 2026 - Ciclismo Carretera</h4>
                    <p className="text-muted-foreground">Ciclismo de carretera fluido por las mejores rutas de Ibiza con vistas abiertas al Mediterráneo.</p>
                  </div>
                </div>
                <p className="font-bold text-foreground">
                  Lo técnico te hace fuerte. La velocidad te hace ágil. El equilibrio te hace épico.
                </p>
              </div>
              
              {/* Características del evento */}
              <div className="mt-8 p-6 bg-accent/5 rounded-2xl border border-accent/20">
                <div className="grid gap-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-accent mt-0.5 shrink-0" />
                    <div>
                      <p className="font-semibold">Ruta Mountain Bike técnica y selectiva en Ibiza</p>
                      <p className="text-sm text-muted-foreground">111 km de senderos con 2.500 m+ de desnivel por la naturaleza ibicenca</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-accent mt-0.5 shrink-0" />
                    <div>
                      <p className="font-semibold">Ruta ciclismo carretera estratégica por Ibiza</p>
                      <p className="text-sm text-muted-foreground">135 km con 2.285 m+ de desnivel y vistas al Mediterráneo</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-accent mt-0.5 shrink-0" />
                    <div>
                      <p className="font-semibold">Clasificación combinada Balanced Ride</p>
                      <p className="text-sm text-muted-foreground">Suma de tiempos de ambas etapas: Mountain Bike + Carretera</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-accent mt-0.5 shrink-0" />
                    <span className="text-foreground font-medium">Abierto a ciclistas nacionales e internacionales</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-accent mt-0.5 shrink-0" />
                    <div className="text-foreground font-medium">
                      <span className="block">Categorías: Individual femenina y masculina, parejas mixtas o parejas por géneros.</span>
                      <span className="block text-sm text-muted-foreground mt-1 italic">
                        (En estas 2 últimas categorías cada participante correrá una de las pruebas sumando el tiempo de ambas para la clasificación)
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-8 mt-12">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-primary/10 rounded-full">
                    <Calendar className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="font-mono text-xs uppercase text-muted-foreground">Fecha</p>
                    <p className="font-bold">{EVENT_CONFIG.dates.fullRange}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-primary/10 rounded-full">
                    <MapPin className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="font-mono text-xs uppercase text-muted-foreground">Lugar</p>
                    <p className="font-bold">Ibiza, Baleares</p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={springTransition} className="relative aspect-square">
              <div className="absolute -inset-4 border-2 border-accent/20 rounded-2xl rotate-3" />
              <img src="https://static-us-img.skywork.ai/prod/user/head_picture/2022064447563415552_WhatsApp Image 2026-02-12 at 19.23.16.jpeg?image_process=quality,q_90/resize,w_1280/format,webp" alt="Pelotón ciclistas compitiendo en evento mountain bike y carretera Ibiza - Ambiente competitivo Ibiza Bike Epic" className="w-full rounded-xl shadow-2xl relative z-10 h-[592px] object-cover" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* #recorridos Section */}
      <section id="recorridos" className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-6xl font-black mb-4 uppercase">Recorridos Mountain Bike y Carretera · Ibiza</h2>
            <p className="text-muted-foreground font-mono">DOS DÍAS. DOS DISCIPLINAS. UN MISMO ESPÍRITU COMPETITIVO.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            {EVENT_CONFIG.routes.map((route, idx) => <motion.div key={route.id} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.2
          }}>
                <Card className="group overflow-hidden border-2 border-border hover:border-primary transition-all duration-300 h-full">
                  <div className="h-64 relative overflow-hidden">
                    <img src={route.type === 'senderos' ? IMAGES.CAPTURA_DE_PANTALLA_2026_02_25_A_LAS_12_20_33_121 : IMAGES.CAPTURA_DE_PANTALLA_2026_02_25_A_LAS_12_28_55_120} alt={route.name} className="w-full group-hover:scale-110 transition-transform duration-500 h-[281.6000061035156px] object-cover" />
                    <div className="absolute top-4 left-4">
                      <Badge className={route.type === 'asfalto' ? "bg-blue-500" : "bg-orange-500"}>
                        {route.type.toUpperCase()}
                      </Badge>
                    </div>
                  </div>
                  <CardContent className="p-8">
                    <div className="mb-4">
                      <div className="flex items-center gap-3 mb-2">
                        {route.type === 'asfalto' ? <Bike className="w-8 h-8 text-accent" /> : <Mountain className="w-8 h-8 text-accent" />}
                        <h3 className="text-2xl font-bold">{route.name}</h3>
                      </div>
                      <div className="min-h-[2.5rem] flex items-center">
                        <p className="text-sm font-mono uppercase tracking-wide text-accent font-bold leading-tight">
                          {route.subtitle}
                        </p>
                      </div>
                    </div>
                    <p className="text-muted-foreground mb-8 font-serif leading-relaxed">
                      {route.description}
                    </p>
                    
                    <div className="grid grid-cols-3 gap-4 mb-8">
                      <div className="text-center p-3 bg-muted rounded-lg">
                        <p className="text-[10px] font-mono uppercase text-muted-foreground">Distancia</p>
                        <p className="font-bold">{route.distance}</p>
                      </div>
                      <div className="text-center p-3 bg-muted rounded-lg">
                        <p className="text-[10px] font-mono uppercase text-muted-foreground">Desnivel</p>
                        <p className="font-bold">{route.elevation}</p>
                      </div>
                      <div className="text-center p-3 bg-muted rounded-lg">
                        <p className="text-[10px] font-mono uppercase text-muted-foreground">Dificultad</p>
                        <p className="font-bold">{route.difficulty}</p>
                      </div>
                    </div>

                    <ul className="space-y-3 mb-6">
                      {route.features.map((feature, fIdx) => <li key={fIdx} className="flex items-center gap-2 text-sm">
                          <Activity className="w-4 h-4 text-accent" />
                          <span>{feature}</span>
                        </li>)}
                    </ul>
                    
                    {/* Imagen del recorrido */}
                    <div className="mb-4">
                      <img src={route.id === 'mtb-epic' ? IMAGES.CAPTURA_DE_PANTALLA_2026_02_26_A_LAS_10_18_21_133 : IMAGES.CAPTURA_DE_PANTALLA_2026_02_26_A_LAS_10_22_22_134} alt={`Vista previa del recorrido ${route.name} - Mapa de la ruta`} className="w-full h-auto rounded-lg border border-border shadow-sm hover:shadow-md transition-shadow duration-300" />
                    </div>
                    
                    {/* Botón para ver recorrido completo */}
                    <Button variant="outline" className="w-full mt-4 border-accent text-accent hover:bg-accent/10 transition-all duration-300 group" onClick={() => window.open(route.mapUrl, '_blank')}>
                      <Map className="w-4 h-4 mr-2 group-hover:scale-110 transition-transform" />
                      Ver Recorrido Completo
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* #reserva Section */}
      <section id="reserva" className="py-24 bg-gradient-to-br from-primary/5 via-accent/5 to-secondary/5 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-72 h-72 bg-accent/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-primary/10 rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={springTransition}>
              <div className="flex items-center justify-center gap-3 mb-6">
                <img src={IMAGES.LOGO_RESERVA} alt="Logo" className="w-10 h-10 object-contain" />
                <h2 className="text-4xl md:text-6xl font-black uppercase tracking-tighter">
                  Reserva tu <span className="text-accent">Dorsal</span>
                </h2>
              </div>
              <p className="text-xl font-serif text-muted-foreground max-w-3xl mx-auto leading-relaxed">
                Primera edición. 250 dorsales. Las plazas para la primera edición son limitadas. 
                Cuando se completen las plazas, se cerrará la inscripción.
              </p>
            </motion.div>
          </div>

          {/* Contenido unificado de Reserva tu Dorsal */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={springTransition} className="mb-16">
            <div className="bg-gradient-to-br from-white to-gray-50 dark:from-gray-900 dark:to-gray-800 p-8 md:p-12 rounded-3xl shadow-2xl border border-border">
              
              {/* Información de dorsales */}
              <div className="text-center mb-12">
                <div className="inline-flex items-center gap-2 bg-accent/10 text-accent px-4 py-2 rounded-full text-sm font-mono uppercase tracking-wide mb-4">
                  <Zap className="w-4 h-4 text-accent" />
                  Oferta Limitada
                </div>
                <h3 className="text-3xl font-black mb-2">Primera Edición</h3>
                <div className="text-6xl font-black text-accent mb-2">250</div>
                <p className="text-muted-foreground font-serif">dorsales totales</p>
              </div>

              {/* Precios */}
              <div className="space-y-4 mb-12">
                <div className="flex justify-between items-center p-4 bg-muted/50 rounded-xl">
                  <div>
                    <span className="font-medium block">Dorsal Individual</span>
                    <span className="text-sm text-muted-foreground">Ambas rutas (MTB + Carretera)</span>
                  </div>
                  <span className="font-bold text-accent text-xl">€120</span>
                </div>
                <div className="flex justify-between items-center p-4 bg-muted/50 rounded-xl">
                  <div>
                    <span className="font-medium block">Dorsal Parejas</span>
                    <span className="text-sm text-muted-foreground">Cada participante una ruta</span>
                  </div>
                  <div className="text-right">
                    <div className="flex items-baseline gap-1 justify-end">
                      <span className="font-bold text-accent text-xl">80€</span>
                      <span className="text-xs text-muted-foreground font-medium">x pax</span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 opacity-75">
                      Total: 160€ (2 personas)
                    </div>
                  </div>
                </div>
                <div className="text-center text-sm text-muted-foreground font-serif">
                  * Precios Early Bird válidos hasta el 31 de marzo 2026
                </div>
              </div>

              {/* ¿Qué incluye tu dorsal? */}
              <div className="mb-12">
                <div className="text-center mb-8">
                  <h3 className="text-3xl md:text-4xl font-black mb-4 text-foreground">
                    ¿Qué incluye tu <span className="text-accent">dorsal</span>?
                  </h3>
                  <p className="text-lg text-muted-foreground font-serif">
                    Tu inscripción te da acceso completo al Balanced Ride Weekend:
                  </p>
                </div>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="flex items-start gap-3 p-4 bg-accent/5 rounded-xl">
                      <div className="p-2 bg-accent/10 rounded-full shrink-0 mt-1">
                        <CheckCircle className="w-5 h-5 text-accent" />
                      </div>
                      <div>
                        <h4 className="font-bold text-foreground">Participación en la prueba MTB</h4>
                        <p className="text-sm text-muted-foreground">Sábado 31 de octubre - 111km</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3 p-4 bg-accent/5 rounded-xl">
                      <div className="p-2 bg-accent/10 rounded-full shrink-0 mt-1">
                        <CheckCircle className="w-5 h-5 text-accent" />
                      </div>
                      <div>
                        <h4 className="font-bold text-foreground">Participación en la prueba de carretera</h4>
                        <p className="text-sm text-muted-foreground">Domingo 1 de noviembre - 135km</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3 p-4 bg-accent/5 rounded-xl">
                      <div className="p-2 bg-accent/10 rounded-full shrink-0 mt-1">
                        <CheckCircle className="w-5 h-5 text-accent" />
                      </div>
                      <div>
                        <h4 className="font-bold text-foreground">Clasificación combinada "Balanced Ride"</h4>
                        <p className="text-sm text-muted-foreground">Ranking oficial del fin de semana</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3 p-4 bg-accent/5 rounded-xl">
                      <div className="p-2 bg-accent/10 rounded-full shrink-0 mt-1">
                        <CheckCircle className="w-5 h-5 text-accent" />
                      </div>
                      <div>
                        <h4 className="font-bold text-foreground">Cronometraje oficial</h4>
                        <p className="text-sm text-muted-foreground">Tiempos precisos y resultados en vivo</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="flex items-start gap-3 p-4 bg-accent/5 rounded-xl">
                      <div className="p-2 bg-accent/10 rounded-full shrink-0 mt-1">
                        <CheckCircle className="w-5 h-5 text-accent" />
                      </div>
                      <div>
                        <h4 className="font-bold text-foreground">Seguro y cobertura médica</h4>
                        <p className="text-sm text-muted-foreground">Protección completa durante el evento</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3 p-4 bg-accent/5 rounded-xl">
                      <div className="p-2 bg-accent/10 rounded-full shrink-0 mt-1">
                        <CheckCircle className="w-5 h-5 text-accent" />
                      </div>
                      <div>
                        <h4 className="font-bold text-foreground">Avituallamientos durante las pruebas</h4>
                        <p className="text-sm text-muted-foreground">Puntos de hidratación y nutrición</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3 p-4 bg-accent/5 rounded-xl">
                      <div className="p-2 bg-accent/10 rounded-full shrink-0 mt-1">
                        <CheckCircle className="w-5 h-5 text-accent" />
                      </div>
                      <div>
                        <h4 className="font-bold text-foreground">Bolsa oficial del evento</h4>
                        <p className="text-sm text-muted-foreground">Kit de bienvenida y recuerdos</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3 p-4 bg-accent/5 rounded-xl">
                      <div className="p-2 bg-accent/10 rounded-full shrink-0 mt-1">
                        <CheckCircle className="w-5 h-5 text-accent" />
                      </div>
                      <div>
                        <h4 className="font-bold text-foreground">Maillot oficial del evento</h4>
                        <p className="text-sm text-muted-foreground">Camiseta técnica conmemorativa</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Botón de inscripción al final */}
              <div className="text-center">
                <Button size="lg" className="w-full bg-gradient-to-r from-accent to-primary text-white hover:from-accent/90 hover:to-primary/90 text-base md:text-xl py-4 md:py-8 px-4 md:px-6 rounded-2xl font-black uppercase tracking-widest transition-all duration-300 transform hover:scale-105 active:scale-95 shadow-lg hover:shadow-xl" onClick={() => window.open(EVENT_CONFIG.registrationUrl, '_blank')}>
                  <Trophy className="w-4 h-4 md:w-6 md:h-6 mr-2 md:mr-3 text-accent" />
                  <span className="hidden sm:inline">Reserva tu Dorsal</span>
                  <span className="sm:hidden">Reservar</span>
                </Button>
                
                <div className="mt-4 md:mt-6">
                  <p className="text-xs text-muted-foreground font-mono uppercase tracking-wide text-center">
                    <span className="hidden md:inline">Inscripción segura • Confirmación inmediata • Cancelación gratuita hasta 30 días antes</span>
                    <span className="md:hidden">Inscripción segura • Confirmación inmediata</span>
                  </p>
                </div>
              </div>
            </div>
          </motion.div>


        </div>
      </section>

      {/* #ibiza-otono Section */}
      <section id="ibiza-otono" className="py-24 bg-gradient-to-br from-primary/5 via-background to-accent/5 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-primary/10 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 left-1/4 w-80 h-80 bg-accent/10 rounded-full blur-3xl" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            <motion.div initial={{
            opacity: 0,
            x: -50
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={springTransition} className="order-2 lg:order-1">
              <div className="mb-6 lg:mb-8 text-center lg:text-left">
                <h2 className="text-3xl md:text-4xl lg:text-6xl font-black mb-4 lg:mb-6 uppercase tracking-tighter">
                  IBIZA EN <span className="text-accent">OTOÑO</span>
                </h2>
                <h3 className="text-xl md:text-2xl lg:text-3xl font-bold mb-6 lg:mb-8 text-muted-foreground font-serif">
                  Corre. Descubre. Quédate.
                </h3>
              </div>
              
              <div className="space-y-4 lg:space-y-6 text-base lg:text-lg text-muted-foreground font-serif leading-relaxed text-center lg:text-left">
                <p>
                  Ibiza ofrece en octubre el escenario perfecto para el ciclismo: temperaturas ideales, 
                  carreteras tranquilas y paisajes únicos entre mar y montaña.
                </p>
                <p>
                  Ibiza Bike Epic es competir…pero también es vivir la isla desde otra perspectiva.
                </p>
                <p className="font-medium text-foreground">
                  Muy pronto estarán disponibles los packs oficiales de transporte y alojamiento 
                  para que puedas disfrutar de la experiencia completa.
                </p>
              </div>
              
              <div className="mt-8 lg:mt-10 flex justify-center lg:justify-start">
                <Button variant="outline" size="default" className="bg-accent/10 border-accent text-accent hover:bg-accent hover:text-accent-foreground transition-all duration-300 px-4 py-3 lg:px-8 lg:py-4 text-sm lg:text-lg font-bold uppercase tracking-wide w-full sm:w-auto" onClick={() => window.open('mailto:info@ibizabikeepic.com?subject=Interés en packs de transporte y alojamiento', '_blank')}>
                  <Mail className="w-4 h-4 lg:w-5 lg:h-5 mr-2 lg:mr-3" />
                  <span className="hidden sm:inline">Escríbenos si estás interesado</span>
                  <span className="sm:hidden">Contactar</span>
                </Button>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={springTransition} className="relative order-1 lg:order-2 mb-8 lg:mb-0">
              <div className="relative aspect-[4/3] lg:aspect-[4/3] overflow-hidden rounded-2xl lg:rounded-3xl">
                <img src={IMAGES.ES_VEDRA_IBIZA_135} alt="Es Vedrà Ibiza - Paisaje icónico de la isla con acantilados y mar Mediterráneo" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent" />
                <div className="absolute bottom-3 left-3 right-3 lg:bottom-6 lg:left-6 lg:right-6">
                  <div className="bg-white/90 dark:bg-black/90 backdrop-blur-sm p-3 lg:p-4 rounded-lg lg:rounded-xl">
                    <p className="text-xs lg:text-sm font-medium text-foreground">
                      Octubre en Ibiza: el momento perfecto para descubrir la isla sobre dos ruedas
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Decorative elements - hidden on mobile */}
              <div className="hidden lg:block absolute -top-4 -right-4 w-24 h-24 bg-accent/20 rounded-full blur-2xl" />
              <div className="hidden lg:block absolute -bottom-4 -left-4 w-32 h-32 bg-primary/20 rounded-full blur-2xl" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* #sostenibilidad Section */}
      <section id="sostenibilidad" className="py-24 bg-gradient-to-br from-accent/5 via-background to-primary/5 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-72 h-72 bg-accent/10 rounded-full blur-3xl translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-primary/10 rounded-full blur-3xl -translate-x-1/2 translate-y-1/2" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={springTransition}>
              <div className="flex items-center justify-center gap-3 mb-6">
                <Leaf className="w-10 h-10 text-accent" />
                <h2 className="text-4xl md:text-6xl font-black uppercase tracking-tighter">
                  Sostenibilidad · <span className="text-accent">Compromiso Real</span>
                </h2>
              </div>
              <p className="text-xl font-serif text-muted-foreground max-w-3xl mx-auto leading-relaxed">
                Rendimiento y responsabilidad. En equilibrio. El equilibrio también es responsabilidad.
              </p>
            </motion.div>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={springTransition} className="bg-white/50 dark:bg-black/20 p-8 rounded-2xl backdrop-blur-sm border border-accent/10">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-accent/10 rounded-full">
                  <Activity className="w-6 h-6 text-accent" />
                </div>
                <h4 className="text-xl font-bold">Huella de Carbono</h4>
              </div>
              <p className="text-muted-foreground font-serif mb-4">
                Medimos nuestro impacto real. Esta primera edición marca el punto de partida para construir un plan de reducción y compensación a cinco años.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="bg-white/50 dark:bg-black/20 p-8 rounded-2xl backdrop-blur-sm border border-accent/10">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-accent/10 rounded-full">
                  <Recycle className="w-6 h-6 text-accent" />
                </div>
                <h4 className="text-xl font-bold">Gestión de Residuos</h4>
              </div>
              <p className="text-muted-foreground font-serif mb-4">
                Cada evento produce materiales y desechos. Queremos analizarlos con rigor y responsabilidad hacia un modelo Residuo Cero.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="bg-white/50 dark:bg-black/20 p-8 rounded-2xl backdrop-blur-sm border border-accent/10">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-accent/10 rounded-full">
                  <Users className="w-6 h-6 text-accent" />
                </div>
                <h4 className="text-xl font-bold">Inclusión y Comunidad</h4>
              </div>
              <p className="text-muted-foreground font-serif mb-4">
                Impulsamos el ciclismo como herramienta de impacto social junto a la comunidad local y jóvenes de la isla.
              </p>
            </motion.div>
          </div>
          
          {/* Call to Action integrado */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.4
        }} className="text-center mt-16">
            <div className="bg-gradient-to-br from-accent/10 via-primary/5 to-accent/5 p-8 md:p-12 rounded-3xl border border-accent/20">
              <div className="mb-6">
                <p className="text-lg font-mono uppercase tracking-widest text-accent font-bold mb-2">
                  31 OCT · 1 NOV 2026
                </p>
                <h3 className="text-3xl md:text-5xl font-black mb-4 text-foreground leading-tight">
                  El equilibrio no se explica. Se vive.
                </h3>
                <p className="text-lg text-muted-foreground font-serif max-w-2xl mx-auto">
                  Las plazas para la primera edición son limitadas.
                </p>
              </div>
              
              <Button size="lg" className="bg-gradient-to-r from-accent to-primary text-white hover:from-accent/90 hover:to-primary/90 text-xl py-6 px-12 rounded-2xl font-black uppercase tracking-widest transition-all duration-300 transform hover:scale-105 active:scale-95 shadow-lg hover:shadow-xl" onClick={() => window.open(EVENT_CONFIG.registrationUrl, '_blank')}>
                <Trophy className="w-6 h-6 mr-3" />
                Reserva tu Dorsal
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* #impacto-local Section */}
      <section id="impacto-local" className="py-24 bg-background relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-accent rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-48 h-48 bg-primary rounded-full blur-3xl" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={springTransition}>
              <div className="flex items-center justify-center gap-3 mb-6">
                <Heart className="w-10 h-10 text-accent" />
                <h2 className="text-4xl md:text-6xl font-black uppercase tracking-tighter">
                  Compromiso con <span className="text-accent">Ibiza</span>
                </h2>
              </div>
              <p className="text-xl font-serif text-muted-foreground max-w-3xl mx-auto leading-relaxed">
                Más que un evento deportivo, somos una plataforma estratégica para posicionar a Ibiza como destino deportivo mundial, 
                promoviendo turismo sostenible y generando impacto económico positivo desestacionalizador.
              </p>
            </motion.div>
          </div>

          <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
            <motion.div initial={{
            opacity: 0,
            x: -50
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={springTransition}>
              <div className="space-y-8">
                <div className="flex items-start gap-4 p-6 bg-card rounded-2xl border border-border hover:border-accent/50 transition-colors">
                  <div className="p-3 bg-blue-500/10 rounded-full shrink-0">
                    <TrendingUp className="w-6 h-6 text-accent" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Desestacionalización Turística</h3>
                    <p className="text-muted-foreground font-serif">
                      Promovemos el turismo de calidad en temporada baja (octubre-noviembre), 
                      beneficiando hoteles, restaurantes y comercios locales con ocupación fuera de temporada alta.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-6 bg-card rounded-2xl border border-border hover:border-accent/50 transition-colors">
                  <div className="p-3 bg-green-500/10 rounded-full shrink-0">
                    <Building className="w-6 h-6 text-accent" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Proveedores Locales</h3>
                    <p className="text-muted-foreground font-serif">
                      Priorizamos empresas de Ibiza para logística, catering y servicios. 
                      Nuestro objetivo: que el 70% del presupuesto se quede en la isla.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-6 bg-card rounded-2xl border border-border hover:border-accent/50 transition-colors">
                  <div className="p-3 bg-purple-500/10 rounded-full shrink-0">
                    <Users className="w-6 h-6 text-accent" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-2">Comunidad Ciclista Local</h3>
                    <p className="text-muted-foreground font-serif">
                      Facilitamos la participación de ciclistas baleares con descuentos especiales 
                      y creamos oportunidades para que sean embajadores del evento.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={springTransition} className="relative">
              <div className="absolute -inset-4 border-2 border-accent/20 rounded-3xl -rotate-2" />
              <img src="https://static-us-img.skywork.ai/prod/user/head_picture/2022068133714776064_Captura de pantalla 2026-02-12 a las 22.59.15.png?image_process=quality,q_90/resize,w_1280/format,webp" alt="Paisaje de Ibiza" className="w-full rounded-2xl shadow-2xl relative z-10 h-[396.316650390625px] object-cover" />
            </motion.div>
          </div>

          {/* Iniciativas Sociales */}
          <div className="bg-gradient-to-r from-accent/5 to-primary/5 rounded-3xl p-8 md:p-12">
            <div className="text-center mb-12">
              <h3 className="text-3xl md:text-4xl font-black mb-4 uppercase">Iniciativas Sociales</h3>
              <p className="text-muted-foreground font-serif text-lg">
                Creamos oportunidades para toda la comunidad ibicenca
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.1
            }} className="bg-white/50 dark:bg-black/20 p-8 rounded-2xl backdrop-blur-sm border border-accent/10">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-orange-500/10 rounded-full">
                    <Handshake className="w-6 h-6 text-orange-500" />
                  </div>
                  <h4 className="text-xl font-bold">Ibiza Bike Epic Inclusiva</h4>
                </div>
                <p className="text-muted-foreground font-serif mb-4">
                  Jornadas en colaboración con asociaciones locales para personas con diversidad funcional, 
                  organizando talleres de ciclismo adaptado y rutas accesibles.
                </p>
                <div className="text-sm font-mono text-accent uppercase tracking-wide">
                  • Talleres de habilidad adaptada<br />
                  • Rutas accesibles<br />
                  • Deporte sin barreras
                </div>
              </motion.div>

              <motion.div initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.2
            }} className="bg-white/50 dark:bg-black/20 p-8 rounded-2xl backdrop-blur-sm border border-primary/10">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-blue-500/10 rounded-full">
                    <Target className="w-6 h-6 text-accent" />
                  </div>
                  <h4 className="text-xl font-bold">Ibiza Bike Epic Kids</h4>
                </div>
                <p className="text-muted-foreground font-serif mb-4">
                  Jornada lúdico-educativa en circuito seguro, con talleres prácticos de habilidad y seguridad vial 
                  para inspirar a la próxima generación de ciclistas responsables.
                </p>
                <div className="text-sm font-mono text-primary uppercase tracking-wide">
                  • Circuito seguro para niños<br />
                  • Educación vial<br />
                  • Formación de futuros ciclistas
                </div>
              </motion.div>
            </div>
          </div>

          {/* Métricas de Impacto */}
          <div className="mt-20">
            <h3 className="text-center text-3xl font-black mb-12 uppercase">Nuestro Compromiso Medible</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[{
              number: "70%",
              label: "Presupuesto Local",
              desc: "Invertido en empresas de Ibiza"
            }, {
              number: "Net Zero",
              label: "Huella Carbono",
              desc: "Compensación 100% local"
            }, {
              number: "30%",
              label: "Participación Internacional",
              desc: "Promoción global de Ibiza"
            }, {
              number: "100%",
              label: "Zero Waste",
              desc: "Eliminación plásticos un solo uso"
            }].map((metric, idx) => <motion.div key={idx} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: idx * 0.1
            }} className="text-center p-6 bg-card rounded-xl border border-border hover:border-accent/50 transition-colors">
                  <div className="text-3xl md:text-4xl font-black text-accent mb-2">{metric.number}</div>
                  <div className="font-bold text-sm mb-1">{metric.label}</div>
                  <div className="text-xs text-muted-foreground font-serif">{metric.desc}</div>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>

      {/* #galeria Section */}
      <section id="galeria" className="py-24 bg-card">
        <div className="container mx-auto px-4">
          <h2 className="text-center text-4xl font-black mb-16 uppercase tracking-tighter">Instantáneas de la Épica</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[IMAGES.MONOCHROME_WOMAN_BICYCLE_136, IMAGES.WHATSAPP_IMAGE_06_41_12_139, IMAGES.WHATSAPP_IMAGE_06_40_07_137, IMAGES.WHATSAPP_IMAGE_06_40_42_138].map((img, i) => <motion.div key={i} whileHover={{
            scale: 0.98
          }} className="aspect-square overflow-hidden rounded-lg">
                <img src={img} alt={`Ibiza Bike Epic - Instantánea ${i + 1}`} className="w-full h-full object-cover" />
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* #inscripcion Section */}
      <section id="inscripcion" className="py-32 bg-primary text-primary-foreground text-center relative">
        <div className="absolute inset-0 opacity-10">
          <img src={IMAGES.COMPETITION_3} className="w-full h-full object-cover" alt="Background" />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={springTransition}>
            <h2 className="text-5xl md:text-8xl font-black mb-8 uppercase tracking-tighter">
              EL EQUILIBRIO <br />NO SE EXPLICA. SE VIVE.
            </h2>
            <p className="text-xl md:text-2xl mb-12 text-primary-foreground/80 max-w-2xl mx-auto font-serif italic">
              No es solo una carrera. Es el nacimiento de un nuevo desafío en el calendario ciclista. Dentro de unos años, podrás decir que estuviste en la primera.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-6">
              <Button size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 text-xl py-8 px-12 rounded-full font-black uppercase tracking-widest transition-transform active:scale-95" onClick={() => window.open(EVENT_CONFIG.registrationUrl, '_blank')}>
                Reserva tu Dorsal
              </Button>
            </div>
            
          </motion.div>
        </div>
      </section>

      {/* Footer minimal info */}
      <footer className="py-12 bg-background border-t border-border">
        <div className="container mx-auto px-4 text-center">
          <img src={IMAGES.LOGO_RESERVA} alt="Logo" className="h-12 w-auto mx-auto mb-6" />
          <p className="font-mono text-xs text-muted-foreground uppercase tracking-widest">
            &copy; 2026 IBIZA BIKE EPIC - BALANCED RIDE WEEKEND. TODOS LOS DERECHOS RESERVADOS.
          </p>
        </div>
      </footer>
    </div>;
}